import { autoinject, singleton } from "aurelia-framework";
import { QuestionsDict, SaveQuestionCommentsRequest } from "../../models/onboarding/rightship-onboarding.models";
import { RightShipOnboardingService } from "./rightship-onboarding.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { GenericConfirmationDialogModel } from "../../models/basics/generic-confirmation-dialog-model";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DialogServiceConstants } from "../../constants/basics/dialog.constants";
import { Guid } from "../../utilities/guid";

@singleton()
@autoinject
export class RightShipOnboardingQuestionCommentService {
    
    private questionsDictSnapShot: QuestionsDict = {};
    public questionsDict: QuestionsDict = {}; //comments for questions
    public questionTexts: QuestionsDict = {}; //question titles
    public onboardingRequestId: number;

    public showQuestionComments: boolean = false;

    public messages: { guid: string, question: string, comment: string }[];

    constructor(private rightshipOnboarddingService: RightShipOnboardingService, private dialogClient: DialogClientService) {
    }

    public async loadQuestionComments(onboardingRequestId: number): Promise<void> {
        if(this.hasUnsavedChanges()) {
            this.showUnsavedChangesDialog(async () => {                
                this.clearQuestionComments();
                await this.loadQuestionCommentInternal(onboardingRequestId);
            });
        }
        else {
            await this.loadQuestionCommentInternal(onboardingRequestId);
        }
    }

    private async loadQuestionCommentInternal(onboardingRequestId: number): Promise<void> 
    {
        this.onboardingRequestId = onboardingRequestId;
        this.questionsDict = await this.rightshipOnboarddingService.getQuestionComments(onboardingRequestId);
        this.questionsDictSnapShot = CloneHelper.cloneObject(this.questionsDict);
        this.updateMessagesList();
    }

    public async saveQuestionComments(hide: boolean = false): Promise<void> {
        const questionComments = Object.keys(this.questionsDict).filter(q => this.questionsDict[q] != null).map(q => { return { questionGuid: q, comment: this.questionsDict[q] } });
        const request: SaveQuestionCommentsRequest = {
            onboardingRequestId: this.onboardingRequestId,
            questionComments: questionComments
        };
        await this.rightshipOnboarddingService.saveQuestionComments(request);
        this.questionsDictSnapShot = CloneHelper.cloneObject(this.questionsDict);
        if(hide)
        {
            this.showQuestionComments = false;
        }
    }

    public clearQuestionComments(): void {
        this.questionsDict = {};
        this.questionsDictSnapShot = {};
        this.questionTexts = {};
        this.messages = [];
    }

    private hasUnsavedChanges(): boolean {
        return JSON.stringify(this.questionsDict) != JSON.stringify(this.questionsDictSnapShot);
    }

    public setQuestionComment(questionGuid: Guid, comment: string, questionText: string = null): void {
        this.questionsDict[questionGuid.toString()] = comment;
        if(questionText)
            this.setQuestionText(questionGuid, questionText);
        this.updateMessagesList();
    }

    public setQuestionText(questionGuid: Guid, questionText: string): void {
        this.questionTexts[questionGuid.toString()] = questionText;
        this.updateMessagesList();
    }

    public getQuestionComment(questionGuid: Guid): string {
        if(!this.questionsDict || Object.keys(this.questionsDict).indexOf(questionGuid?.toString()) < 0)
            return null;
        return this.questionsDict[questionGuid.toString()];
    }

    public removeQuestionComment(questionGuid: string): void {
        if(!this.questionsDict || Object.keys(this.questionsDict).indexOf(questionGuid?.toString()) < 0)
            return;
        
        this.questionsDict[questionGuid] = undefined;
        this.updateMessagesList();
    }

    private updateMessagesList()
    {
        this.messages = Object.keys(this.questionsDict).filter(g => this.questionsDict[g]).map(g => {
            return { guid: g, question: this.questionTexts[g], comment: this.questionsDict[g] };
        });
    }

    public canDeactivate(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const clear = () => {            
                this.clearQuestionComments();
                this.onboardingRequestId = null;
                this.showQuestionComments = false;
                resolve(true);
            }; 

            if(this.hasUnsavedChanges()) {
                this.showUnsavedChangesDialog(clear);
            }
            else clear();
        });
    }

    private showUnsavedChangesDialog(callback: () => void | Promise<void>) {
        const model = new GenericConfirmationDialogModel();
        model.confirm = "Yes";
        model.cancel = "No";
        model.messages.push("You have unsaved changes! Do you want to save changes?");
        model.title = "Save Changes";
        model.onConfirm = () => {
            return new Promise<void>((resolve, reject) => {
                this.saveQuestionComments().then(() => {
                    if (callback) {
                        const response = callback();
                        if (response instanceof Promise) {
                            response.then(() => {
                                resolve();
                            }).catch(() => {
                                reject();
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else resolve();
                }).catch(() => {
                    reject();
                });
            });
        };
        model.onCancel = () => {
            callback();
        };

        this.dialogClient.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
    }
}